import React, { useState, useRef } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fullUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${API_URL}${u}`;
};

/**
 * ImageUploader
 * Props:
 *  - value: current image URL (string)
 *  - onChange: (url) => void  — called with the new URL after upload, or "" on clear
 *  - label: optional label text
 *  - testid: optional data-testid prefix
 *
 * Allows drag-and-drop OR click to pick. Uploads to /api/admin/uploads and returns the URL.
 */
export const ImageUploader = ({ value, onChange, label, testid = 'image-uploader', accept = 'image/*' }) => {
  const { authedAxios } = useAdminAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [dragging, setDragging] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setErr('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await authedAxios().post('/api/admin/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div>
      {label && <label className="block text-sm text-[#B8860B] mb-2">{label}</label>}

      {value ? (
        <div
          data-testid={`${testid}-preview`}
          className="relative inline-block rounded-lg border-2 border-[#B8860B]/40 overflow-hidden bg-[#050814]"
        >
          {/* Preview of uploaded image */}
          <img
            src={fullUrl(value)}
            alt="Uploaded"
            className="block max-h-48 max-w-full"
          />
          <button
            type="button"
            data-testid={`${testid}-clear`}
            onClick={() => onChange('')}
            className="absolute top-1 right-1 bg-[#050814]/80 hover:bg-red-900 text-[#F4F1EA] rounded-full w-7 h-7 flex items-center justify-center"
            title="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div
          data-testid={`${testid}-dropzone`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`flex flex-col items-center justify-center gap-2 py-6 px-4 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
            dragging
              ? 'border-[#B8860B] bg-[#B8860B]/10'
              : 'border-[#B8860B]/30 bg-[#050814] hover:border-[#B8860B] hover:bg-[#B8860B]/5'
          }`}
        >
          {uploading ? (
            <Loader2 className="animate-spin text-[#B8860B]" size={28} />
          ) : (
            <Upload className="text-[#B8860B]" size={28} />
          )}
          <p className="text-sm text-[#F4F1EA]">
            {uploading ? 'Uploading...' : 'Click to upload or drag & drop'}
          </p>
          <p className="text-xs text-[#F4F1EA]/50">JPG, PNG, WEBP, GIF · 8 MB max</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onPick}
        className="hidden"
        data-testid={`${testid}-input`}
      />

      {err && (
        <p data-testid={`${testid}-error`} className="text-xs text-red-400 mt-2 flex items-center gap-1">
          <ImageIcon size={12} /> {err}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
